import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { camelCase } from 'camel-case';
import { constantCase } from 'constant-case';
import { DocumentNode } from 'graphql';
import { Observable } from 'rxjs';
import {
  map, tap, take
} from 'rxjs/operators';

import { pluralize } from 'app/utils/pluralize';
import { fragments } from 'graphql/fragments';
import { DocumentVersionInput } from 'graphql/models/generated';
import { DataItem, DataResourceType } from 'models/data.model';
import { StoreService } from 'shared/services/store.service';

@Injectable({
  providedIn: 'root'
})
export class CoreDataBuilderService {
  constructor(
    private storeService: StoreService,
    private apollo: Apollo
  ) { }

  getOne<T>(
    type: DataResourceType,
    variables: Record<string, any>,
    fragmentName?: string
  ): Observable<T> {
    const queryName = camelCase(type);
    const fragmentNameProcessed = fragmentName || `${type}Fragment`;
    const whereInput = `${type}WhereUniqueInput!`;

    const query: DocumentNode = gql`
      query ${queryName}($where: ${whereInput}) {
        ${queryName}(where: $where) {
          ...${fragmentNameProcessed}
        }
      }
      ${fragments[constantCase(fragmentNameProcessed)]}
    `;

    return this.apollo.query({
      query,
      variables,
      fetchPolicy: 'no-cache',
      ...(type === 'Branch' ? { errorPolicy: 'ignore' } : {}) // temp fix for media serice: ignore Branch errors
    })
      .pipe<T, T, T>(
        map((r) => (r.data as any)?.[queryName] as T),
        tap((item: T) => this.storeService.setCurrentDataItem(item as DataItem)),
        take(1)
      );
  }

  getMany<T>(
    type: DataResourceType,
    variables: Record<string, any>,
    fragmentName?: string
  ): Observable<T[]> {
    const queryName = camelCase(pluralize(type));
    const fragmentNameProcessed = fragmentName || `${type}BaseFragment`;
    const whereInput = `${type}WhereInput`;
    const orderByInput = `${type}OrderByInput`;

    const whereBranch = { args: '', subArgs: '' };
    if (variables.whereBranch) {
      whereBranch.args = '$whereBranch: BranchWhereInput, $branchOperator: BranchOperator';
      whereBranch.subArgs = 'whereBranch: $whereBranch, branchOperator: $branchOperator';
    }

    const query: DocumentNode = gql`
      query ${queryName}(
        $take: Int,
        $skip: Int,
        $orderBy: [${orderByInput}!],
        $where: ${whereInput},
        ${whereBranch.args}
      ) {
        ${queryName}(
          take: $take
          skip: $skip
          orderBy: $orderBy
          where: $where
          ${whereBranch.subArgs}
        ) {
          ...${fragmentNameProcessed}
        }
      }
      ${fragments[constantCase(fragmentNameProcessed)]}
    `;

    return this.apollo.query({
      query,
      variables,
      fetchPolicy: 'no-cache'
    })
      .pipe<T[], T[]>(
        map((r) => (r.data as any)?.[queryName] as T[]),
        take(1)
      );
  }

  update<T, U>(
    type: DataResourceType,
    id: string,
    data: U,
    fragmentName?: string
  ): Observable<T> {
    const mutationName = `update${type}`;
    const fragmentNameProcessed = fragmentName || `${type}Fragment`;

    const mutation: DocumentNode = gql`
      mutation ${mutationName}($id: String!, $data: ${type}UpdateInput!) {
        ${mutationName}(where: { id: $id }, data: $data) {
          ...${fragmentNameProcessed}
        }
      }
      ${fragments[constantCase(fragmentNameProcessed)]}
    `;

    return this.apollo.mutate({
      mutation,
      variables: { id, data },
      fetchPolicy: 'no-cache'
    })
      .pipe<T, T, T, T>(
        map((r) => (r.data as any)?.[mutationName] as T),
        tap((item: T) => this.storeService.setCurrentDataItem(item as DataItem)),
        tap(() => this.storeService.setTableUpdate()),
        take(1)
      );
  }

  upsert<T, U>(
    type: DataResourceType,
    id: string,
    data: U,
    fragmentName?: string
  ): Observable<T> {
    const mutationName = `upsert${type}`;
    const fragmentNameProcessed = fragmentName || `${type}Fragment`;

    const mutation: DocumentNode = gql`
      mutation ${mutationName}($id: String!, $data: ${type}UpdateInput!) {
        ${mutationName}(where: { id: $id }, create: $data, update: $data) {
          ...${fragmentNameProcessed}
        }
      }
      ${fragments[constantCase(fragmentNameProcessed)]}
    `;

    return this.apollo.mutate({
      mutation,
      variables: { id, data },
      fetchPolicy: 'no-cache'
    })
      .pipe<T, T, T, T>(
        map((r) => (r.data as any)?.[mutationName] as T),
        tap((item: T) => this.storeService.setCurrentDataItem(item as DataItem)),
        tap(() => this.storeService.setTableUpdate()),
        take(1)
      );
  }

  create<T, U>(
    type: DataResourceType,
    formValues: Record<string, any>,
    documentVersion?: DocumentVersionInput
  ): Observable<T> {
    if (documentVersion) {
      return this.createAndLinkToQuote(type, formValues, documentVersion);
    }

    const data = this.buildDataInput<U>(formValues);

    const mutationName = `create${type}`;
    const fragmentName = `${type}BaseFragment`;

    const mutation = gql`
      mutation ${mutationName}($data: ${type}CreateInput!) {
        ${mutationName}(data: $data) {
          ...${fragmentName}
        }
      }
      ${fragments[constantCase(fragmentName)]}
    `;

    return this.apollo.mutate({
      mutation,
      variables: { data },
      fetchPolicy: 'no-cache'
    })
      .pipe<T, T, T, T>(
        map((r) => (r.data as any)?.[mutationName] as T),
        tap((item: T) => this.storeService.setCurrentDataItem(item as DataItem)),
        tap(() => this.storeService.setTableUpdate()),
        take(1)
      );
  }

  delete<T>(
    type: DataResourceType,
    id: string
  ): Observable<T> {
    const mutationName = `delete${type}`;

    const mutation: DocumentNode = gql`
      mutation ${mutationName}($id: String!) {
        ${mutationName}(where: { id: $id }) {
          id
        }
      }
    `;

    return this.apollo.mutate({
      mutation,
      variables: { id },
      fetchPolicy: 'no-cache'
    })
      .pipe<T, T, T>(
        map((r) => (r.data as any)?.[mutationName] as T),
        tap(() => this.storeService.setTableUpdate()),
        take(1)
      );
  }

  private createAndLinkToQuote<T, U>(
    type: DataResourceType,
    formValues: Record<string, any>,
    documentVersion: DocumentVersionInput
  ): Observable<T> {
    if (type === 'Object') {
      formValues.address.id = null;
    }
    const data = this.buildDataInput<U>(formValues);

    const mutationName = `create${type}AndLinkToQuote`;
    const fragmentName = `${type}BaseFragment`;
    const coreDataType: string = type.charAt(0).toLowerCase() + type.slice(1);

    const mutation: DocumentNode = gql`
        mutation ${mutationName}($data: ${type}CreateInput!, $documentVersion: DocumentVersionInput!) {
          ${mutationName}(data: $data, documentVersion: $documentVersion) {
          ...${fragmentName}
          }
        }
        ${fragments[constantCase(fragmentName)]}
      `;

    return this.apollo.mutate({
      mutation,
      variables: { data, documentVersion },
      fetchPolicy: 'no-cache'
    })
      .pipe<T, T, T, T>(
        map((r) => (r.data as any)?.[mutationName] as T),
        tap((item: T) => this.storeService.setCurrentDataItem(item as DataItem)),
        tap((item: T) => this.storeService.setOfferUpdate({ [coreDataType]: item })),
        take(1)
      );
  }

  buildDataInput<T>(formValue: Record<string, any>, isCreateInput = true): T {
    const props: string[] = Object.keys(formValue);
    const filteredProps = isCreateInput
      ? props.filter((p) => typeof formValue[p] !== 'undefined' && formValue[p] !== null)
      : props;

    return filteredProps
      .reduce((obj, prop) => {
        let value;
        if (prop === 'id' && formValue[prop] === '') {
          // Skip empty 'id' property
          return obj;
        }
        if (prop === 'columnsVisibility') {
          return { columnsVisibility: JSON.stringify(formValue[prop]) };
        }

        if (this.isArray(formValue[prop])) {
          if (this.isArrayOfObjects(formValue[prop])) {
            value = {};
            // get 'create' data inputs (fields without IDs)
            const createInputs = formValue[prop]
              .filter((i: any) => !i.id)
              .reduce((arr: any[], i: any) => {
                const input = this.buildDataInput<Record<string, any>>(i);
                // deleteOnUpdate should not be present in the 'data' input
                delete input.deleteOnUpdate;

                return Object.keys(input).length ? [...arr, input] : arr;
              }, []);
            if (createInputs.length) {
              value = { ...value, create: createInputs };
            }
            // get 'update' data inputs (fields with IDs)
            const updateInputs = formValue[prop]
              .filter((i: any) => i.id && !i.deleteOnUpdate)
              .reduce((arr: any[], i: any) => {
                const input = this.buildDataInput<Record<string, any>>(i, false);
                // ID should not be present in the 'data' input,
                // since its relation is defined in the 'where' below
                delete input.id;
                delete input.deleteOnUpdate;

                return [...arr, { where: { id: i.id }, data: input }];
              }, []);
            if (updateInputs.length) {
              value = { ...value, update: updateInputs };
            }
            // get 'delete' data inputs (fields with IDs and 'deleteOnUpdate' flag set to true)
            const deleteInputs = formValue[prop]
              .filter((i: any) => i.id && i.deleteOnUpdate)
              .reduce((arr: any[], i: any) => [...arr, { id: i.id }], []);
            if (deleteInputs.length) {
              value = { ...value, delete: deleteInputs };
            }
          } else { // array of primitives
            value = { set: formValue[prop] };
          }
        } else if (this.isObject(formValue[prop])) {
          if (formValue[prop].connect || formValue[prop].disconnect) {
            value = formValue[prop];
          } else {
            const input = this.buildDataInput<Record<string, any>>(formValue[prop]);
            if (Object.keys(input).length) {
              if (!input.id) {
                value = { create: input };
              } else {
                delete input.id;
                const updateInput = this.buildDataInput<Record<string, any>>(input, false);
                value = { update: updateInput };
              }
            }
          }
        } else if (typeof formValue[prop] === 'string' && formValue[prop].trim() === '') {
          value = isCreateInput ? '' : { set: '' };
        } else {
          value = !isCreateInput ? { set: formValue[prop] } : formValue[prop];
        }

        return (value !== undefined) ? { ...obj, [prop]: value } : obj;
      }, {}) as T;
  }

  private isArrayOfObjects(arr: any[]): boolean {
    return arr.length > 0 && arr.every((i: any) => this.isObject(i));
  }

  private isArray(val: any): boolean {
    return Object.prototype.toString.call(val) === '[object Array]';
  }

  private isObject(val: any): boolean {
    return Object.prototype.toString.call(val) === '[object Object]';
  }
}
